import React, { useCallback, useEffect, useRef, useState } from "react"
import { classNames } from "../Components"
import { Block, BlockField, BlockRow } from "./Block"
import { Loading } from "./Loading"
import { Overlay } from "./Overlay"
import { logger } from "../App/Logger"

interface ConfirmDialogProps {
  // When true, await onConfirm: show a spinner and keep the dialog open until the action
  // resolves, surfacing `errorMessage` if it rejects. When false (default) onConfirm is
  // fired and the parent is expected to close the dialog right away.
  awaitConfirm?: boolean;
  cancelLabel: string;
  // When true, render the dialog vertically centered in the middle of the screen
  // instead of the standard fixed offset near the top. Used for confirmations
  // where the trigger and the confirm action must not land in the same screen
  // position (e.g. a reflexive double-tap), such as the man-overboard trigger.
  centered?: boolean;
  confirmLabel: string;
  // When true, render the Confirm button disabled (e.g. the action is not permitted for
  // the current state). The dialog stays open so the message can explain why.
  confirmDisabled?: boolean;
  errorMessage?: string;
  // When true, the dialog is still preparing (e.g. awaiting a "can create?" check): show a
  // spinner inside the Overlay and hide the message/buttons until the parent clears it.
  loading?: boolean;
  message: React.ReactNode;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
  style?: string;
  title?: string;
}

// Reusable in-app confirmation dialog. Renders inside an Overlay and reports the user's
// choice through the onConfirm (accept) and onCancel (reject) callbacks. The parent
// controls visibility by conditionally rendering this component.
export function ConfirmDialog({ awaitConfirm, cancelLabel, centered, confirmLabel, confirmDisabled, errorMessage, loading, message, onCancel, onConfirm, style, title }: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const confirm = useCallback(async () => {
    if (!awaitConfirm) {
      void onConfirm();
      return;
    }
    if (busy) {
      return;
    }
    setBusy(true);
    setFailed(false);
    try {
      await onConfirm();
    } catch (error) {
      logger.error(error);
      if (mounted.current) {
        setFailed(true);
      }
    } finally {
      if (mounted.current) {
        setBusy(false);
      }
    }
  }, [awaitConfirm, busy, onConfirm]);

  const cancel = useCallback(() => {
    if (!busy) {
      onCancel();
    }
  }, [busy, onCancel]);

  return (
    <Overlay className={classNames(["ConfirmDialog", centered && "overlay-centered"])} closeBackground={!busy} closeCallback={cancel} style={style || "warning"} title={title}>
      <div className="Overlay-body">
        <Block>
          {loading ? (
            <BlockRow><BlockField><Loading /></BlockField></BlockRow>
          ) : (
            <>
              <BlockRow>{message}</BlockRow>
              {failed && errorMessage && (
                <BlockRow className="ConfirmDialog-error text-error">{errorMessage}</BlockRow>
              )}
              <BlockRow actions={true}>
                {busy ? (
                  <BlockField><Loading /></BlockField>
                ) : (
                  <>
                    <BlockField>
                      <button className="button" onClick={() => { cancel(); }}>{cancelLabel}</button>
                    </BlockField>
                    <BlockField>
                      <button className="button default" disabled={confirmDisabled} onClick={() => { confirm(); }}>{confirmLabel}</button>
                    </BlockField>
                  </>
                )}
              </BlockRow>
            </>
          )}
        </Block>
      </div>
    </Overlay>
  );
}
